import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, LogOut, ChevronDown, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  const loadNotifs = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data || []);
    } catch {}
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 20000);
    return () => clearInterval(t);
  }, [user]);

  const unread = notifs.filter((n) => !n.read).length;

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); loadNotifs(); } catch {}
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#030A06]/80 border-b border-[#122E1C]">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to={user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/"} className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/nasarawa-logo.jpg" alt="Nasarawa Expo" className="h-10 w-10 object-contain bg-white p-0.5 rounded-sm" />
          <div className="flex flex-col leading-none">
            <span className="font-display font-bold tracking-tight text-white">Nasarawa Expo</span>
            <span className="text-[10px] font-mono-jb text-[#8A9F90] uppercase tracking-widest">Arts · Music · Fashion</span>
          </div>
        </Link>

        <div className="flex items-center gap-3">
          {!user && (
            <>
              <Link to="/creatives" data-testid="nav-creatives-link" className="text-sm text-white/80 hover:text-white px-3 py-2 hidden sm:inline">Creators</Link>
              <Link to="/login" data-testid="nav-login-link" className="text-sm text-white/80 hover:text-white px-3 py-2">Sign in</Link>
              <Link to="/register" data-testid="nav-register-link">
                <Button data-testid="nav-register-btn" className="bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none">
                  Become a Creative
                </Button>
              </Link>
            </>
          )}

          {user && (
            <>
              <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="notification-bell"
                    className="relative p-2 border border-[#122E1C] hover:border-[#00E676]/50 transition-colors"
                    aria-label="Notifications"
                  >
                    <Bell className="w-4 h-4 text-white" />
                    {unread > 0 && (
                      <span data-testid="notification-unread-count" className="absolute -top-1 -right-1 bg-[#00E676] text-[#030A06] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                        {unread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-96 bg-[#07140B] border-[#122E1C] text-white rounded-none">
                  <DropdownMenuLabel className="font-display tracking-tight">Notifications</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#122E1C]" />
                  {notifs.length === 0 && (
                    <div className="px-4 py-6 text-center text-[#8A9F90] text-sm">No notifications yet.</div>
                  )}
                  {notifs.slice(0, 10).map((n) => (
                    <div
                      key={n.id}
                      data-testid={`notification-item-${n.id}`}
                      onClick={() => markRead(n.id)}
                      className={`px-3 py-3 border-b border-[#122E1C] cursor-pointer hover:bg-[#0D2114] ${!n.read ? "bg-[#0D2114]/60" : ""}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold flex items-center gap-2">
                            {!n.read && <span className="w-1.5 h-1.5 bg-[#00E676] rounded-full" />}
                            {n.title}
                          </div>
                          <div className="text-xs text-[#8A9F90] mt-1">{n.body}</div>
                          {n.external_link && (
                            <a href={n.external_link} target="_blank" rel="noreferrer" className="text-xs text-[#00E676] hover:underline mt-1 inline-block" data-testid="notif-external-link">
                              Join group →
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 border border-[#122E1C] px-3 py-2 hover:border-[#00E676]/50">
                    <div className="w-6 h-6 bg-[#0D2114] flex items-center justify-center text-xs font-mono-jb text-[#00E676]">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <span className="text-sm hidden sm:inline">{user.name}</span>
                    <ChevronDown className="w-3 h-3 text-[#8A9F90]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#07140B] border-[#122E1C] text-white rounded-none">
                  <DropdownMenuLabel className="text-[#8A9F90] text-xs uppercase tracking-widest font-mono-jb">
                    {user.role === "admin" ? "Admin" : "Creative"}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#122E1C]" />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")} className="cursor-pointer hover:bg-[#0D2114] focus:bg-[#0D2114]">
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }} className="cursor-pointer text-[#FF3B30] hover:bg-[#0D2114] focus:bg-[#0D2114]">
                    <LogOut className="w-3 h-3 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
